import { useEffect, useRef } from 'react';
import {
  Reply,
  Forward,
  Copy,
  Pencil,
  Trash2,
  Globe,
  Link,
} from 'lucide-react';
import type { TelegramMessage } from '../types';

interface ContextMenuProps {
  message: TelegramMessage;
  position: { x: number; y: number };
  onClose: () => void;
  onReply: (message: TelegramMessage) => void;
  onForward: (message: TelegramMessage) => void;
  onCopy: (message: TelegramMessage) => void;
  onEdit: (message: TelegramMessage) => void;
  onDelete: (message: TelegramMessage) => void;
  onMarkPublic: (message: TelegramMessage) => void;
  onLinkToOrder: (message: TelegramMessage) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  divider?: boolean;
}

export function ContextMenu({
  message,
  position,
  onClose,
  onReply,
  onForward,
  onCopy,
  onEdit,
  onDelete,
  onMarkPublic,
  onLinkToOrder,
  canEdit = false,
  canDelete = false,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position to keep menu in viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (rect.right > viewportWidth) {
        menuRef.current.style.left = `${position.x - rect.width}px`;
      }

      if (rect.bottom > viewportHeight) {
        menuRef.current.style.top = `${position.y - rect.height}px`;
      }
    }
  }, [position]);

  const menuItems: MenuItem[] = [
    {
      icon: <Reply className="h-4 w-4" />,
      label: 'Ответить',
      onClick: () => {
        onReply(message);
        onClose();
      },
    },
    {
      icon: <Forward className="h-4 w-4" />,
      label: 'Переслать',
      onClick: () => {
        onForward(message);
        onClose();
      },
    },
    {
      icon: <Copy className="h-4 w-4" />,
      label: 'Копировать',
      onClick: () => {
        onCopy(message);
        onClose();
      },
      disabled: !message.text,
    },
    {
      icon: <Pencil className="h-4 w-4" />,
      label: 'Редактировать',
      onClick: () => {
        onEdit(message);
        onClose();
      },
      disabled: !canEdit,
      divider: true,
    },
    {
      icon: <Globe className="h-4 w-4" />,
      label: message.isPublic ? 'Снять публичность' : 'Сделать публичным',
      onClick: () => {
        onMarkPublic(message);
        onClose();
      },
    },
    {
      icon: <Link className="h-4 w-4" />,
      label: 'Привязать к заказу',
      onClick: () => {
        onLinkToOrder(message);
        onClose();
      },
      divider: true,
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      label: 'Удалить',
      onClick: () => {
        onDelete(message);
        onClose();
      },
      danger: true,
      disabled: !canDelete,
    },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[200px] bg-popover border rounded-lg shadow-lg py-1 animate-in fade-in-0 zoom-in-95"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {menuItems.map((item, index) => (
        <div key={index}>
          {item.divider && index > 0 && (
            <div className="h-px bg-border my-1" />
          )}
          <button
            className={`
              w-full flex items-center gap-3 px-3 py-2 text-sm
              transition-colors
              ${item.disabled
                ? 'text-muted-foreground cursor-not-allowed'
                : (item.danger
                  ? 'text-destructive hover:bg-destructive/10'
                  : 'hover:bg-accent')
              }
            `}
            onClick={item.onClick}
            disabled={item.disabled}
          >
            {item.icon}
            {item.label}
          </button>
        </div>
      ))}
    </div>
  );
}
